import React, { useMemo } from "react";
import WallSegmentRenderer from "./WallSegmentRenderer";
import CeilingLightRenderer from "./CeilingLightRenderer";
import StairsRenderer from "./StairsRenderer";
import AdaptiveFrameRenderer from "./AdaptiveFrameRenderer";
import {
  GRID_CELL_SIZE_M,
  POTENTIAL_WALL_SLOT_COLOR,
  OBJECT_TYPES,
  EPSILON,
  WALL_THICKNESS_M_RENDER,
  INITIAL_PPM,
} from "../configuratorConstants";
import { ReactComponent as FullFrameSvg } from '../../Assets/frame.svg';
import { ReactComponent as BigModuleFrameSvg } from '../../Assets/bigmoduleframe.svg';
import { ReactComponent as EmptyPlanSvg } from '../../Assets/plans/План Пустий.svg';
import { ReactComponent as CorridorPlanX1Svg } from '../../Assets/plans/План Коридорний - х1.svg';
import { ReactComponent as CorridorPlanX2Svg } from '../../Assets/plans/План Коридорний - х2.svg';
import { ReactComponent as ShowerPlanSvg } from '../../Assets/plans/План Душовий.svg';
import { ReactComponent as ToiletPlanSvg } from '../../Assets/plans/План _Туалет.svg';
import { ReactComponent as ResidentialUniversalPlanSvg } from '../../Assets/plans/План_житловий універсальний.svg';
import { ReactComponent as SanitaryCombinedPlanSvg } from '../../Assets/plans/План_Санітарний комбінований.svg';
import { ReactComponent as KitchenPlanSvg } from '../../Assets/plans/план_кухня.svg';
import { ReactComponent as LaundryPlanSvg } from '../../Assets/plans/план_пральня.svg';
import { ReactComponent as LivingPlanSvg } from '../../Assets/plans/житловий план.svg';

// Кастомные размеры фрейма для модуля "Великий базовий"
// Измените эти значения, чтобы настроить размеры рамки для этого модуля
const LARGE_BASIC_MODULE_FRAME_WIDTH_MULTIPLIER = 1.7;  // Множитель ширины фрейма
const LARGE_BASIC_MODULE_FRAME_HEIGHT_MULTIPLIER = 1; // Множитель высоты фрейма

const ModuleRenderer = ({
  module,
  scale,
  selectedObjectId,
  setSelectedObjectId,
  onToggleWallSegment,
  primarySelectedObject,
  onContextMenu,
  elementTypeToPlace,
  onWallSegmentClick,
  currentFloorIndex,
  isGhost = false,
}) => {
  const {
    x, y, cellsWide, cellsLong, rotation, wallSegments, label, id: moduleId, freeElements = [], subtype, templateId,
  } = module;

  const isStairsModule = subtype === 'stairs';
  const isOutdoorStairs = isStairsModule && templateId && templateId.includes('outdoor');
  const isIndoorStairs = isStairsModule && templateId && templateId.includes('indoor');
  const isFloor2Stairs = isStairsModule && templateId && templateId.includes('floor2');

  const getModulePlanType = () => {
    if (subtype === 'corridor') {
      if (cellsWide === 2 && cellsLong === 2) return 'corridor_x1';
      if (cellsWide >= 4 && cellsLong === 2) return 'corridor_x2';
      return 'adaptive';
    }

    // Визначаємо стандартні розміри модулів (в клітинках)
    const isStandardSize =
      (cellsWide === 2 && cellsLong === 5) || // 2x5 базовий
      (cellsWide === 5 && cellsLong === 2) || // 5x2 базовий горизонтальний
      (cellsWide === 3 && cellsLong === 5) || // 3x5 великий базовий
      (cellsWide === 5 && cellsLong === 3);   // 5x3 великий базовий горизонтальний

    // Якщо це нестандартний розмір модуля - використовуємо адаптивний фрейм
    if (!isStandardSize && !isStairsModule) {
      return 'adaptive';
    }

    if (subtype === 'shower') return 'shower';
    if (subtype === 'toilet') return 'toilet';
    if (subtype === 'residential_universal') return 'residential_universal';
    if (subtype === 'sanitary_combined') return 'sanitary_combined';
    if (subtype === 'kitchen') return 'kitchen';
    if (subtype === 'laundry') return 'laundry';
    if (subtype === 'living_plan') return 'living_plan';

    if (!isStairsModule && (!wallSegments || Object.keys(wallSegments).length === 0) && (!freeElements || freeElements.length === 0)) {
      return 'empty';
    }

    return 'empty';
  };

  const moduleWidthM = module.width;
  const moduleHeightM = module.height;
  const isModuleEffectivelyVertical = moduleHeightM > moduleWidthM + EPSILON;

  const moduleTransform = `translate(${x * scale}, ${y * scale}) rotate(${rotation || 0})`;
  const cellSizePx = GRID_CELL_SIZE_M * scale;

  const moduleInnerWidthM = cellsWide * GRID_CELL_SIZE_M;
  const moduleInnerHeightM = cellsLong * GRID_CELL_SIZE_M;
  const moduleGridWidthPx = moduleInnerWidthM * scale;
  const moduleGridHeightPx = moduleInnerHeightM * scale;

  const invScaleFactor = INITIAL_PPM / scale;

  const fullFrameStyle = {
    fill: "none",
    stroke: "rgba(107, 114, 128, 0.6)",
    strokeWidth: 0.75,
    strokeDasharray: "4, 2",
    pointerEvents: "none",
  };

  const handleModuleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onContextMenu) {
      onContextMenu(e, moduleId, OBJECT_TYPES.MODULE);
    }
  };

  const strokeWidth = (selectedObjectId === moduleId ? 1.2 : 0.8) * invScaleFactor;

  // Helper function to render label with line wrapping
  const renderModuleLabel = () => {
    if (!label || isGhost) return null;

    const words = label.split(' ');
    const fontSize = 0.25 * scale;
    const lineHeight = fontSize * 1.2;

    // Single word - render as single line
    if (words.length === 1) {
      return (
        <text
          x={moduleGridWidthPx / 2}
          y={moduleGridHeightPx / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#374151"
          fontSize={fontSize}
          fontWeight="500"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {label}
        </text>
      );
    }

    // Multiple words - split into two lines at the middle
    const midIndex = Math.ceil(words.length / 2);
    const lines = [
      words.slice(0, midIndex).join(' '),
      words.slice(midIndex).join(' ')
    ];

    // Calculate starting Y to center the text block vertically
    const totalHeight = lineHeight * (lines.length - 1);
    const startY = moduleGridHeightPx / 2 - totalHeight / 2;

    return (
      <text
        x={moduleGridWidthPx / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fill="#374151"
        fontSize={fontSize}
        fontWeight="500"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {lines.map((line, index) => (
          <tspan
            key={index}
            x={moduleGridWidthPx / 2}
            y={startY + index * lineHeight}
          >
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  const handleSlotClick = (cellX, cellY, orientation) => {
    if (elementTypeToPlace === OBJECT_TYPES.INTERNAL_WALL_TOOL) {
      let isPerimeter = false;
      const cLong = parseInt(cellsLong) || 0;
      const cWide = parseInt(cellsWide) || 0;

      if (orientation === "h" && (cellY === 0 || cellY === cLong)) isPerimeter = true;
      if (orientation === "v" && (cellX === 0 || cellX === cWide)) isPerimeter = true;

      if (!isPerimeter) {
        if (typeof onToggleWallSegment === 'function') {
          onToggleWallSegment(moduleId, cellX, cellY, orientation);
        } else {
          console.error("ModuleRenderer: onToggleWallSegment is not a function!");
        }
      } else {
        console.log("ModuleRenderer: Clicked on a perimeter slot, no action for INTERNAL_WALL_TOOL.");
      }
    }
  };

  const isSegmentSuitableForPlacement = (segmentData, currentElementTypeToPlace, parentModule, segmentKey) => {
    if (parentModule.disallowedWallSegments?.includes(segmentKey)) {
      return false;
    }
    if (!currentElementTypeToPlace || !segmentData) return false;
    if (currentElementTypeToPlace === OBJECT_TYPES.INTERNAL_WALL_TOOL) return false;

    // Для жалюзи не подсвечиваем стены - подсвечиваются только окна
    if (currentElementTypeToPlace === OBJECT_TYPES.WINDOW_BLINDS) {
      return false;
    }

    if (segmentData.elements?.some(el => el.type === OBJECT_TYPES.DOOR)) {
      return false;
    }
    if (currentElementTypeToPlace === OBJECT_TYPES.DOOR && currentFloorIndex > 0) {
      const cLong = parseInt(cellsLong) || 0;
      const cWide = parseInt(cellsWide) || 0;
      const isExternal = (segmentData.orientation === 'h' && (segmentData.cellY === 0 || segmentData.cellY === cLong)) ||
        (segmentData.orientation === 'v' && (segmentData.cellX === 0 || segmentData.cellX === cWide));
      if (isExternal) return false;
    }

    // Panoramic windows require 2 segments, so they cannot be placed on the last segment
    if (currentElementTypeToPlace === OBJECT_TYPES.PANORAMIC_WINDOW) {
      const cLong = parseInt(cellsLong) || 0;
      const cWide = parseInt(cellsWide) || 0;

      if (segmentData.orientation === 'h') {
        // Horizontal: cannot place on the last column (needs space to the right)
        if (segmentData.cellX >= cWide - 1) return false;
      } else {
        // Vertical: cannot place on the last row (needs space below)
        if (segmentData.cellY >= cLong - 1) return false;
      }
    }

    return true;
  };

  const potentialWallSlots = [];
  const showSlotsThreshold = 5; // Понижен порог для лучшей видимости
  if (cellSizePx > showSlotsThreshold && elementTypeToPlace === OBJECT_TYPES.INTERNAL_WALL_TOOL) {
    const cLong = parseInt(cellsLong) || 0;
    const cWide = parseInt(cellsWide) || 0;

    // Горизонтальные слоты (стены)
    for (let cy = 1; cy < cLong; cy++) {
      for (let cx = 0; cx < cWide; cx++) {
        const segmentKey = `${cx},${cy}_h`;
        const isExistingWall = !!wallSegments[segmentKey];

        // Увеличенная область клика
        const clickAreaHeight = Math.max(10, cellSizePx * 0.15); // Минимум 10px или 15% от размера ячейки
        const lineY = cy * cellSizePx;
        const clickAreaY = lineY - clickAreaHeight / 2;

        potentialWallSlots.push(
          <g key={`slot-h-${cx}-${cy}`}>
            {/* Невидимая область для клика */}
            <rect
              x={cx * cellSizePx}
              y={clickAreaY}
              width={cellSizePx}
              height={clickAreaHeight}
              fill="transparent"
              onClick={() => handleSlotClick(cx, cy, "h")}
              className="cursor-pointer"
              style={{ pointerEvents: 'all' }}
              onMouseEnter={(e) => {
                const line = e.currentTarget.nextSibling;
                if (line) {
                  line.setAttribute('stroke', isExistingWall ? 'rgba(255,50,50,0.9)' : 'rgba(59, 130, 246, 0.9)');
                  line.setAttribute('stroke-width', Math.max(3, cellSizePx * 0.08));
                }
              }}
              onMouseLeave={(e) => {
                const line = e.currentTarget.nextSibling;
                if (line) {
                  line.setAttribute('stroke', isExistingWall ? 'rgba(255,0,0,0.5)' : POTENTIAL_WALL_SLOT_COLOR);
                  line.setAttribute('stroke-width', Math.max(2, cellSizePx * 0.06));
                }
              }}
            />
            {/* Видимая линия */}
            <line
              x1={cx * cellSizePx}
              y1={lineY}
              x2={(cx + 1) * cellSizePx}
              y2={lineY}
              stroke={isExistingWall ? "rgba(255,0,0,0.5)" : POTENTIAL_WALL_SLOT_COLOR}
              strokeWidth={Math.max(2, cellSizePx * 0.06)}
              strokeDasharray={isExistingWall ? "" : "4,2"}
              style={{ pointerEvents: 'none', transition: 'all 0.15s ease' }}
            />
          </g>
        );
      }
    }

    // Вертикальные слоты (стены)
    for (let cx = 1; cx < cWide; cx++) {
      for (let cy = 0; cy < cLong; cy++) {
        const segmentKey = `${cx},${cy}_v`;
        const isExistingWall = !!wallSegments[segmentKey];

        // Увеличенная область клика
        const clickAreaWidth = Math.max(10, cellSizePx * 0.15); // Минимум 10px или 15% от размера ячейки
        const lineX = cx * cellSizePx;
        const clickAreaX = lineX - clickAreaWidth / 2;

        potentialWallSlots.push(
          <g key={`slot-v-${cx}-${cy}`}>
            {/* Невидимая область для клика */}
            <rect
              x={clickAreaX}
              y={cy * cellSizePx}
              width={clickAreaWidth}
              height={cellSizePx}
              fill="transparent"
              onClick={() => handleSlotClick(cx, cy, "v")}
              className="cursor-pointer"
              style={{ pointerEvents: 'all' }}
              onMouseEnter={(e) => {
                const line = e.currentTarget.nextSibling;
                if (line) {
                  line.setAttribute('stroke', isExistingWall ? 'rgba(255,50,50,0.9)' : 'rgba(59, 130, 246, 0.9)');
                  line.setAttribute('stroke-width', Math.max(3, cellSizePx * 0.08));
                }
              }}
              onMouseLeave={(e) => {
                const line = e.currentTarget.nextSibling;
                if (line) {
                  line.setAttribute('stroke', isExistingWall ? 'rgba(255,0,0,0.5)' : POTENTIAL_WALL_SLOT_COLOR);
                  line.setAttribute('stroke-width', Math.max(2, cellSizePx * 0.06));
                }
              }}
            />
            {/* Видимая линия */}
            <line
              x1={lineX}
              y1={cy * cellSizePx}
              x2={lineX}
              y2={(cy + 1) * cellSizePx}
              stroke={isExistingWall ? "rgba(255,0,0,0.5)" : POTENTIAL_WALL_SLOT_COLOR}
              strokeWidth={Math.max(2, cellSizePx * 0.06)}
              strokeDasharray={isExistingWall ? "" : "4,2"}
              style={{ pointerEvents: 'none', transition: 'all 0.15s ease' }}
            />
          </g>
        );
      }
    }
  }

  const wallSegmentsToRender = Object.entries(wallSegments || {}).map(([segmentKey, segmentData]) => {
    const [coords, orientation] = segmentKey.split("_");
    const [cxStr, cyStr] = coords.split(",");
    const cellX = parseInt(cxStr, 10);
    const cellY = parseInt(cyStr, 10);
    let selectedElementIdOnThisWall = null;
    if (primarySelectedObject && primarySelectedObject.parentWallSegment && primarySelectedObject.parentWallSegment.id === segmentData.id) {
      if (primarySelectedObject.type !== OBJECT_TYPES.WALL_SEGMENT) {
        selectedElementIdOnThisWall = primarySelectedObject.id;
      }
    }
    const currentSegmentDataWithContext = { ...segmentData, cellX, cellY, orientation };
    const highlight = elementTypeToPlace && elementTypeToPlace !== OBJECT_TYPES.INTERNAL_WALL_TOOL ?
      isSegmentSuitableForPlacement(currentSegmentDataWithContext, elementTypeToPlace, module, segmentKey) : false;
    return (
      <WallSegmentRenderer
        key={segmentData.id || segmentKey}
        segmentData={segmentData}
        moduleObj={module}
        cellX={cellX} cellY={cellY} orientation={orientation} scale={scale}
        isSelected={selectedObjectId === segmentData.id}
        selectedElementId={selectedElementIdOnThisWall}
        onSelectWallSegment={(id) => {
          console.log('🖱️ [ModuleRenderer] onSelectWallSegment called:', {
            id,
            segmentKey,
            elementTypeToPlace,
            hasOnWallSegmentClick: !!onWallSegmentClick
          });
          if (elementTypeToPlace && elementTypeToPlace !== OBJECT_TYPES.INTERNAL_WALL_TOOL) {
            console.log('✅ [ModuleRenderer] Calling onWallSegmentClick with:', { segmentKey, segmentData });
            if (onWallSegmentClick) onWallSegmentClick(segmentKey, { segmentData });
          } else if (!elementTypeToPlace) {
            console.log('📌 [ModuleRenderer] Setting selected object ID:', segmentData.id);
            setSelectedObjectId(segmentData.id);
          }
        }}
        onSelectElement={setSelectedObjectId}
        onContextMenu={onContextMenu}
        highlightForPlacement={highlight}
        isGhost={isGhost}
        isInPlacementMode={!!elementTypeToPlace && elementTypeToPlace !== OBJECT_TYPES.INTERNAL_WALL_TOOL}
        currentFloorIndex={currentFloorIndex}
        elementTypeToPlace={elementTypeToPlace}
      />
    );
  });

  const freeElementsToRender = freeElements.map(element => {
    if (element.type === OBJECT_TYPES.LIGHT_CEILING) {
      const elX = element.x * scale;
      const elY = element.y * scale;
      return (
        <g key={element.id} transform={`translate(${elX}, ${elY})`}>
          <CeilingLightRenderer
            element={element}
            scale={scale}
            isSelected={selectedObjectId === element.id}
          />
        </g>
      );
    }
    return null;
  });

  const renderModuleFrame = () => {
    // Проверяем, является ли это модулем "Великий базовий"
    const isLargeBasicModule = templateId === 'large_basic_module_3x5' || label === 'Великий базовий';

    let frameOffset = 0.025 * scale;
    let frameX = -frameOffset;
    let frameY = -frameOffset;
    let fullFrameWidthPx = (moduleWidthM * scale) + (frameOffset * 2);
    let fullFrameHeightPx = (moduleHeightM * scale) + (frameOffset * 2);

    // Для модуля "Великий базовий" используем специальный фрейм bigmoduleframe.svg
    // Вычисляем размеры рамки с учетом множителей для правильного обволакивания
    if (isLargeBasicModule) {
      const baseWidth = moduleWidthM * scale;
      const baseHeight = moduleHeightM * scale;

      // Применяем множители для создания рамки, которая обволакивает модуль
      const frameWidthPx = baseWidth * LARGE_BASIC_MODULE_FRAME_WIDTH_MULTIPLIER;
      const frameHeightPx = baseHeight * LARGE_BASIC_MODULE_FRAME_HEIGHT_MULTIPLIER;

      // Вычисляем отступы для центрирования модуля внутри рамки
      const frameOffsetX = (frameWidthPx - baseWidth) / 2;
      const frameOffsetY = (frameHeightPx - baseHeight) / 2;

      // Используем максимальный отступ для frameOffset (для совместимости)
      frameOffset = Math.max(frameOffsetX, frameOffsetY, 0.025 * scale);
      frameX = -frameOffsetX;
      frameY = -frameOffsetY;
      fullFrameWidthPx = frameWidthPx;
      fullFrameHeightPx = frameHeightPx;
    }

    let finalWidth = fullFrameWidthPx;
    let finalHeight = fullFrameHeightPx;
    let rotationTransform = "";

    if (isModuleEffectivelyVertical) {
      finalWidth = fullFrameHeightPx;
      finalHeight = fullFrameWidthPx;
      rotationTransform = `translate(${finalHeight},0) rotate(90)`;
    }

    const planType = getModulePlanType();
    let PlanComponent = FullFrameSvg;
    let needsRotation = false;
    let usePlanStyle = false;

    // Для модуля "Великий базовий" используем специальный фрейм
    if (isLargeBasicModule) {
      PlanComponent = BigModuleFrameSvg;
      needsRotation = true; // поворачиваем фрейм на -90 градусов
      usePlanStyle = true;
    } else switch (planType) {
      case 'empty':
        PlanComponent = EmptyPlanSvg;
        needsRotation = true;
        usePlanStyle = true;
        break;
      case 'corridor_x1':
        PlanComponent = CorridorPlanX1Svg;
        needsRotation = true;
        usePlanStyle = true;
        break;
      case 'corridor_x2':
        PlanComponent = CorridorPlanX2Svg;
        needsRotation = true;
        usePlanStyle = true;
        break;
      case 'shower':
        PlanComponent = ShowerPlanSvg;
        needsRotation = true;
        usePlanStyle = true;
        break;
      case 'toilet':
        PlanComponent = ToiletPlanSvg;
        needsRotation = true;
        usePlanStyle = true;
        break;
      case 'residential_universal':
        PlanComponent = ResidentialUniversalPlanSvg;
        needsRotation = true;
        usePlanStyle = true;
        break;
      case 'sanitary_combined':
        PlanComponent = SanitaryCombinedPlanSvg;
        needsRotation = true;
        usePlanStyle = true;
        break;
      case 'kitchen':
        PlanComponent = KitchenPlanSvg;
        needsRotation = true;
        usePlanStyle = true;
        break;
      case 'laundry':
        PlanComponent = LaundryPlanSvg;
        needsRotation = true;
        usePlanStyle = true;
        break;
      case 'living_plan':
        PlanComponent = LivingPlanSvg;
        needsRotation = true;
        usePlanStyle = true;
        break;
      default:
        PlanComponent = FullFrameSvg;
        needsRotation = false;
        usePlanStyle = false;
    }

    const planRotationTransform = needsRotation ?
      `${rotationTransform} rotate(-90 ${finalWidth / 2} ${finalHeight / 2})` :
      rotationTransform;

    const planStyle = {
      fill: "none",
      stroke: "rgba(107, 114, 128, 1)",
      strokeWidth: 1,
      strokeDasharray: "none",
      pointerEvents: "none",
    };

    let widthMultiplier = 1.2;
    let heightMultiplier = 2.47;

    if (isLargeBasicModule) {
      // Для модуля "Великий базовий" рамка уже обволакивает модуль через frameOffset
      // Используем множители 1.0, чтобы план занимал всю рамку
      widthMultiplier = 1.0;
      heightMultiplier = 1.0;
    } else if (planType === 'corridor_x1') {
      widthMultiplier = 1.5;
      heightMultiplier = 0.99;
    } else if (planType === 'corridor_x2') {
      widthMultiplier = 1.3;
      heightMultiplier = 1.97;
    } else if (planType === 'shower' || planType === 'toilet' || planType === 'residential_universal' || planType === 'sanitary_combined' || planType === 'kitchen' || planType === 'laundry' || planType === 'living_plan') {
      widthMultiplier = 1.2;
      heightMultiplier = 2.47;
    }

    const planWidth = usePlanStyle ? finalWidth * widthMultiplier : finalWidth;
    const planHeight = usePlanStyle ? finalHeight * heightMultiplier : finalHeight;
    const planX = usePlanStyle ? (finalWidth - planWidth) / 2 : 0;
    const planY = usePlanStyle ? (finalHeight - planHeight) / 2 : 0;

    // Для адаптивного фрейму використовуємо спеціальний компонент
    if (planType === 'adaptive') {
      return (
        <g
          transform={`translate(${frameX}, ${frameY})`}
          style={{ pointerEvents: 'none' }}
        >
          <AdaptiveFrameRenderer
            width={fullFrameWidthPx}
            height={fullFrameHeightPx}
            scale={scale}
            strokeColor="rgba(107, 114, 128, 0.8)"
            strokeWidth={Math.max(1, scale * 0.02)}
          />
        </g>
      );
    }

    return (
      <g
        transform={`translate(${frameX}, ${frameY})`}
        style={{ pointerEvents: 'none' }}
      >
        <g transform={planRotationTransform}>
          <PlanComponent
            x={planX}
            y={planY}
            width={planWidth}
            height={planHeight}
            style={usePlanStyle ? planStyle : fullFrameStyle}
            preserveAspectRatio={usePlanStyle ? "xMidYMid meet" : "none"}
          />
        </g>
      </g>
    );
  };

  const renderGenericModuleContent = () => (
    <>
      {renderModuleFrame()}
      <rect
        x={strokeWidth / 2}
        y={strokeWidth / 2}
        width={moduleGridWidthPx - strokeWidth}
        height={moduleGridHeightPx - strokeWidth}
        fill={selectedObjectId === moduleId ? "rgba(0, 123, 255, 0.05)" : "rgba(229, 231, 235, 0.5)"}
        stroke={selectedObjectId === moduleId ? "rgba(0, 123, 255, 0.5)" : "rgba(203, 213, 225, 0.7)"}
        strokeWidth={strokeWidth}
        pointerEvents="all"
      />
      {renderModuleLabel()}
      {potentialWallSlots}
      {wallSegmentsToRender}
      {freeElementsToRender}
    </>
  );

  const renderIndoorStairsContent = () => (
    <>
      {renderModuleFrame()}
      <rect
        x={0}
        y={0}
        width={moduleGridWidthPx}
        height={moduleGridHeightPx}
        fill={selectedObjectId === moduleId ? "rgba(0, 123, 255, 0.1)" : "transparent"}
        stroke={selectedObjectId === moduleId ? "rgba(0, 123, 255, 0.5)" : "none"}
        strokeWidth={1.2 * invScaleFactor}
        pointerEvents="all"
      />
      {wallSegmentsToRender}
      <StairsRenderer module={module} scale={scale} />
      {renderModuleLabel()}
    </>
  );

  const renderOutdoorStairsContent = () => (
    <>
      {renderModuleFrame()}
      <rect
        x={0}
        y={0}
        width={moduleGridWidthPx}
        height={moduleGridHeightPx}
        fill="transparent"
        pointerEvents="all"
      />
      <StairsRenderer module={module} scale={scale} />
    </>
  );

  let content;
  if (isIndoorStairs) {
    content = renderIndoorStairsContent();
  } else if (isOutdoorStairs) {
    content = renderOutdoorStairsContent();
  } else {
    content = renderGenericModuleContent();
  }

  return (
    <g
      transform={moduleTransform}
      onContextMenu={isGhost ? undefined : handleModuleContextMenu}
      data-object-id={moduleId}
      data-object-type={OBJECT_TYPES.MODULE}
      data-module-x={x}
      data-module-y={y}
      data-module-rotation={rotation || 0}
      data-module-width-m={moduleWidthM}
      data-module-height-m={moduleHeightM}
      className={isGhost ? "cursor-default" : (selectedObjectId === moduleId ? "cursor-move" : "cursor-pointer")}
      onClick={isGhost ? undefined : () => setSelectedObjectId(moduleId)}
    >
      {content}
    </g>
  );
};
export default React.memo(ModuleRenderer);